import css from "./CompoundItem.sass?inline"
import {isDefined, Lifecycle, Nullable, UUID} from "@opendaw/lib-std"
import {createElement} from "@opendaw/lib-jsx"
import {Events, Html} from "@opendaw/lib-dom"
import {IndexedBox} from "@opendaw/lib-box"
import {PresetEntry} from "@opendaw/studio-core"
import {IconSymbol} from "@opendaw/studio-enums"
import {AnyDragData} from "@/ui/AnyDragData"
import {DragAndDrop} from "@/ui/DragAndDrop"
import {PresetService} from "@/ui/browse/PresetService"
import {DeviceDropKind} from "@/ui/browse/DeviceItem"
import {PresetItems} from "@/ui/browse/PresetItem"
import {Icon} from "../components/Icon"

const className = Html.adoptStyleSheet(css, "CompoundItem")

type RackPayload = {instrumentUuid: UUID.String, effectUuids: ReadonlyArray<UUID.String>}

const rackPayload = (dragData: AnyDragData): Nullable<RackPayload> => {
    if (dragData.type === "instrument" && dragData.device === null) {
        return {instrumentUuid: dragData.uuid, effectUuids: dragData.effects}
    }
    if ((dragData.type === "audio-effect" || dragData.type === "midi-effect")
        && dragData.uuids !== null
        && isDefined(dragData.instrument)) {
        return {instrumentUuid: dragData.instrument, effectUuids: dragData.uuids}
    }
    return null
}

const isRackIntentEffect = (dragData: AnyDragData): boolean =>
    (dragData.type === "audio-effect" || dragData.type === "midi-effect")
    && dragData.uuids !== null
    && isDefined(dragData.instrument)

type Construct = {
    presetService: PresetService
    expandedKeys: Set<string>
    label: string
    icon: IconSymbol
    presets: ReadonlyArray<PresetEntry>
    expandOnRender: boolean
    dropKind: Nullable<DeviceDropKind>
    onDrop: Nullable<(effects: ReadonlyArray<IndexedBox>) => Promise<void>>
    onRackDrop: Nullable<(instrumentUuid: UUID.String, effectUuids: ReadonlyArray<UUID.String>) => Promise<void>>
    expandKey: string
    lifecycle: Lifecycle
}

export const CompoundItem = ({
                                 presetService, expandedKeys, label, icon, presets, expandOnRender,
                                 dropKind, onDrop, onRackDrop, expandKey, lifecycle
                             }: Construct): HTMLElement => {
    const empty = presets.length === 0
    const item: HTMLElement = <div className={Html.buildClassList(className, empty && "empty")}/>
    const header: HTMLElement = (
        <div className="compound-header">
            <span className="triangle"/>
            <div className="icon">
                <Icon symbol={icon}/>
            </div>
            <span className="name">{label}</span>
            <span className="brief"/>
        </div>
    )
    const presetList: HTMLElement = <div className="preset-list hidden"/>
    presetList.append(...PresetItems(presets, presetService, lifecycle))
    const shouldExpand = !empty && (expandedKeys.has(expandKey) || expandOnRender)
    if (shouldExpand) {
        presetList.classList.remove("hidden")
        item.classList.add("expanded")
    }
    if (!empty) {
        lifecycle.own(Events.subscribeSingleClick(header, () => {
            const open = !presetList.classList.toggle("hidden")
            item.classList.toggle("expanded", open)
            if (open) {expandedKeys.add(expandKey)} else {expandedKeys.delete(expandKey)}
        }))
    }
    const acceptsEffectChain = isDefined(dropKind) && isDefined(onDrop)
    const acceptsRack = isDefined(onRackDrop)
    if (acceptsEffectChain || acceptsRack) {
        DragAndDrop.installTarget(header, {
            drag: (_event, dragData) => {
                if (acceptsEffectChain && !isRackIntentEffect(dragData)) {
                    if (presetService.resolveEffectBoxesFromDrag(dropKind, dragData).length > 0) {return true}
                }
                if (acceptsRack && isDefined(rackPayload(dragData))) {return true}
                return false
            },
            drop: (_event, dragData) => {
                if (acceptsEffectChain && !isRackIntentEffect(dragData)) {
                    const effects = presetService.resolveEffectBoxesFromDrag(dropKind, dragData)
                    if (effects.length > 0) {
                        onDrop(effects).catch(console.warn)
                        header.classList.remove("accept-drop")
                        return
                    }
                }
                if (acceptsRack) {
                    const payload = rackPayload(dragData)
                    if (isDefined(payload)) {
                        onRackDrop(payload.instrumentUuid, payload.effectUuids).catch(console.warn)
                    }
                }
                header.classList.remove("accept-drop")
            },
            enter: allowDrop => header.classList.toggle("accept-drop", allowDrop),
            leave: () => header.classList.remove("accept-drop")
        })
    }
    item.appendChild(header)
    item.appendChild(presetList)
    return item
}
